import { Hub } from '@aws-amplify/core';
import { Typography, CircularProgress, Box, Paper, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthContext } from '@/context/AuthProvider';
import { useUserRole } from '@/hooks/useUserRole';
import { useTranslationPrefix } from '@/translations/i18n';
import { isUserProfileIncomplete } from '@/utils/user';

const OAuthCallback = () => {
  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const t = useTranslationPrefix('oauth_callback');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    const errorDescription = params.get('error_description');

    if (errorParam) {
      setStatus('error');
      setErrorMessage(errorDescription || t('error.auth_error', { error: errorParam }));
      return;
    }

    const authListener = Hub.listen('auth', (data) => {
      const { payload } = data;

      switch (payload.event) {
        case 'signedIn':
          navigate('/', { replace: true });
          break;
        case 'signInWithRedirect_failure':
          setStatus('error');
          setErrorMessage(payload.message || t('error.auth_failed'));
          break;
      }
    });

    if (user) {
      if ((isAdvisor || canAccessAdmin) && isUserProfileIncomplete(user)) {
        navigate('/user/update-profile', { replace: true, state: { hideTopNav: true } });
      } else {
        navigate('/', { replace: true });
      }
    }

    return () => {
      authListener();
    };
  }, [navigate, user, isAdvisor, canAccessAdmin, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default',
      }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', borderRadius: '25px' }}>
        <Stack spacing={3} alignItems="center">
          {status === 'processing' && (
            <>
              <CircularProgress />
              <Typography variant="h6">{t('processing.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('processing.message')}
              </Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <Typography variant="h6" color="error">
                {t('error.title')}
              </Typography>
              <Typography variant="body2" color="error">
                {errorMessage || t('error.default_message')}
              </Typography>
              <Typography variant="body2">{t('error.try_again')}</Typography>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default OAuthCallback;
